// @flow

import * as React from 'react';
import {useState} from 'react';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import Group from 'sail/Group';
import {Text, Body} from 'sail/Text';
import {TooltipContainer} from 'sail/Tooltip';
import {useAccount, useLocation} from 'docs/data';
import {getLoginUrl} from 'docs/util';
import {defineMessages, FormattedMessage} from 'react-intl';
import {InlineLink} from 'sail/InlineLink';

import './KeyToken.scss';

import type {Account} from 'docs/data';

const messages = defineMessages({
  type: {
    id: 'docsShell.codeBlock.keyToken.type',
    description: 'Comment in the code sample explaining the type of API key',
    defaultMessage:
      'This is your {type} test mode <a>API key</a>. <b>View your other keys</b>.',
  },
  loggedOut: {
    id: 'docsShell.codeBlock.keyToken.loggedOut',
    description:
      'Comment in the code sample explaining the that this is a test API key and warning users that if they send personal data to Stripe with this API key it will be accessible by all Stripe users.',
    defaultMessage:
      'This is a public <strong>sample</strong> {type} test mode <a>API key</a>. Don’t submit any personally identifiable information in requests made with this key.',
  },
  prompt: {
    id: 'docsShell.codeBlock.keyToken.prompt',
    description:
      'Comment in the code sample prompting a user to sign in to see their API keys in the docs.',
    defaultMessage:
      '<b>Sign in</b> to see examples pre-filled with your test keys.',
  },
});

type KeyType = 'publishable' | 'secret';

type LoggedInOverlayProps = {|
  account: Account,
  type: KeyType,
|};

const LoggedInOverlay = ({account, type}: LoggedInOverlayProps) => {
  const viewKeysUrl = `${account.dashboardUrl}/account/apikeys`;
  return (
    <Group>
      <Body>
        <FormattedMessage
          {...messages.type}
          values={{
            type,
            a: (label) => <InlineLink href="/docs/keys">{label}</InlineLink>,
            b: (label) => <InlineLink href={viewKeysUrl}>{label}</InlineLink>,
          }}
        />
      </Body>
    </Group>
  );
};

type LoggedOutOverlayProps = {|
  account: Account,
  type: KeyType,
|};

const LoggedOutOverlay = ({account, type}: LoggedOutOverlayProps) => {
  const {location} = useLocation();
  const loginUrl = getLoginUrl({account, returnUrl: location.toString()});
  return (
    <Group spacing={12}>
      <Body>
        <FormattedMessage
          {...messages.loggedOut}
          values={{
            strong: (label) => <strong>{label}</strong>,
            type,
            a: (label) => <InlineLink href="/docs/keys">{label}</InlineLink>,
          }}
        />
      </Body>
      <Body>
        <FormattedMessage
          {...messages.prompt}
          values={{
            b: (label) => <InlineLink href={loginUrl}>{label}</InlineLink>,
          }}
        />
      </Body>
    </Group>
  );
};

type KeyTokenProps = {|
  children: React.Node,
  className?: string,
  type: KeyType,
  // The value prop is required since it what is used by renderCode.js
  // to create the contents of what is copied to clipboard
  value: string,
|};

function middleTruncate(str) {
  if (typeof str === 'string') {
    if (str.length > 90) {
      const truncatedText = `${str.substr(0, 28)}...${str.substr(
        str.length - 20,
        str.length,
      )}`;
      return (
        <>
          <span className="KeyToken--truncated">{truncatedText}</span>
          <span className="KeyToken--shadowText">{str}</span>
        </>
      );
    }
  }
  return str;
}

export const KeyToken = ({children, className, type, value}: KeyTokenProps) => {
  const {account} = useAccount();

  const [isDisplayingCopied, setIsDisplayingCopied] = useState(false);

  const body = (
    <span
      className={classNames('KeyToken', className)}
      data-key-type={type}
      onClick={() => {
        copy(value);
        setIsDisplayingCopied(true);
        setTimeout(() => {
          setIsDisplayingCopied(false);
        }, 2000);
      }}
      role="button"
    >
      {middleTruncate(children)}
    </span>
  );

  let overlay;
  if (isDisplayingCopied) {
    overlay = <Text size={13}>Copied!</Text>;
  } else if (account.merchant?.isLoggedIn) {
    overlay = <LoggedInOverlay account={account} type={type} />;
  } else {
    overlay = <LoggedOutOverlay account={account} type={type} />;
  }

  return (
    <TooltipContainer
      allowMouseOverContents
      context={body}
      tooltipContents={overlay}
      hideDelay={250}
      style={{display: 'inline'}}
      contextBoxStyle={{display: 'inline'}}
    />
  );
};
